import ApiClient from './apiClient'

export type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  objects: T[]
  totalCount: number
  pageNo: number | string
}

export type ClientSummary = {
  id: number
  name: string
  display: string
}

export type ObjectUserSummary = {
  id: string
  ceph_uid: string | null
  deleted: boolean
  disabled: boolean
}

export type ServiceSummary = {
  id: number
  display_name: string
  status: string
  created_at: string
  terminated_at: string | null
}

export type ObjectUserServiceSummary = ServiceSummary & {
  domain_name: string | null
}

export type StaffBucket = {
  id: string
  name: string
  access: string
  num_objects: number
  usage: number
  created_at: string
  updated_at: string
  object_user: ObjectUserSummary
  client: ClientSummary | null
  service: ServiceSummary | null
}

export type Quota = {
  enabled: boolean
  check_on_raw: boolean
  max_size: number
  max_size_kb: number
  max_objects: number
}

export type QuotaUpdate = {
  enabled?: boolean
  max_size_kb?: number
  max_objects?: number
}

export type DisabledReason = 'need_payment' | 'admin_locked'

export type StaffObjectUserListItem = {
  id: string
  ceph_uid: string | null
  deleted: boolean
  disabled: boolean
  disabled_reason: DisabledReason | null
  created_at: string
  updated_at: string
  client: ClientSummary
  service: ObjectUserServiceSummary | null
  bucket_count: number
}

export type StaffObjectUserDetails = StaffObjectUserListItem & {
  display_name: string | null
  email: string | null
  suspended: boolean | null
  max_buckets: number | null
  user_quota: Quota | null
  bucket_quota: Quota | null
  keys_count: number
}

export type ObjectUserUpdate = {
  display_name?: string
  email?: string
  max_buckets?: number
  disabled?: boolean
  disabled_reason?: DisabledReason | null
  user_quota?: QuotaUpdate
  bucket_quota?: QuotaUpdate
}

export type SecretKey = {
  access_key: string
  secret_key: string
}

export type LifecycleRule = {
  Expiration?: {
    Date?: string
    Days?: number
    ExpiredObjectDeleteMarker?: boolean
  }
  ID?: string
  Prefix: string
  Status: 'Enabled' | 'Disabled'
  Transition?: {
    Date?: string
    Days?: number
    StorageClass: 'GLACIER' | 'STANDARD_IA' | 'ONEZONE_IA' | 'INTELLIGENT_TIERING' | 'DEEP_ARCHIVE' | 'GLACIER_IR'
  }
  NoncurrentVersionTransition?: {
    NoncurrentDays: number
    StorageClass: 'GLACIER' | 'STANDARD_IA' | 'ONEZONE_IA' | 'INTELLIGENT_TIERING' | 'DEEP_ARCHIVE' | 'GLACIER_IR'
    NewerNoncurrentVersions?: number
  }
  NoncurrentVersionExpiration?: {
    NoncurrentDays: number
    NewerNoncurrentVersions?: number
  }
  AbortIncompleteMultipartUpload?: {
    DaysAfterInitiation: number
  }
}

export type BucketFolder = {
  Prefix?: string
  [key: string]: unknown
}

export type BucketFile = {
  Key?: string
  LastModified?: string
  Size?: number
  StorageClass?: string
  ETag?: string
  [key: string]: unknown
}

export type PathListResponse = {
  prefix: string
  folders: BucketFolder[]
  files: BucketFile[]
  cursor: string | null
}

export type BucketListParams = {
  object_user?: string
  object_user__client?: number
  object_user__service?: number
  name?: string
  access?: string
  search?: string
  ordering?: string
  page?: number | string
  page_size?: number
}

export type ObjectUserListParams = {
  client?: number
  service?: number
  ceph_uid?: string
  deleted?: boolean
  disabled?: boolean
  disabled_reason?: DisabledReason
  search?: string
  ordering?: string
  page?: number | string
  page_size?: number
}

export type FileUpdate = {
  name?: string
  metadata?: Array<{ key: string; value: string }>
  access?: string
  tags?: Array<{ key: string; value: string }>
}

const basePath = 'object-storage'

const encodeNestedPath = (path: string) =>
  path
    .replace(/^\/+/, '')
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/')

const staffObjectStorageService = {
  getBuckets: (params: BucketListParams) =>
    ApiClient.get<PaginatedResponse<StaffBucket>>(`${basePath}/storage`, { params }),

  getBucket: (bucketId: string) =>
    ApiClient.get<StaffBucket>(`${basePath}/storage/${encodeURIComponent(bucketId)}`),

  updateBucket: (bucketId: string, access: string) =>
    ApiClient.put<StaffBucket>(`${basePath}/storage/${encodeURIComponent(bucketId)}`, { access }),

  deleteBucket: (bucketId: string) =>
    ApiClient.delete(`${basePath}/storage/${encodeURIComponent(bucketId)}`),

  getPolicy: (bucketId: string) =>
    ApiClient.get<{ Policy: object | null }>(`${basePath}/storage/${encodeURIComponent(bucketId)}/policy`),

  updatePolicy: (bucketId: string, policy: object | null) =>
    ApiClient.put<object>(`${basePath}/storage/${encodeURIComponent(bucketId)}/policy`, { Policy: policy }),

  getLifecycle: (bucketId: string) =>
    ApiClient.get<{ Rules: LifecycleRule[] | null } | Record<string, unknown>>(
      `${basePath}/storage/${encodeURIComponent(bucketId)}/lifecycle`,
    ),

  updateLifecycle: (bucketId: string, rules: LifecycleRule[]) =>
    ApiClient.put<object>(`${basePath}/storage/${encodeURIComponent(bucketId)}/lifecycle`, rules),

  getPath: (bucketId: string, path = '', cursor?: string, pageSize = 50) => {
    const pathSuffix = path ? `/${encodeNestedPath(path)}` : ''
    return ApiClient.get<PathListResponse>(
      `${basePath}/storage/${encodeURIComponent(bucketId)}/path${pathSuffix}`,
      { params: { cursor, page_size: pageSize } },
    )
  },

  createFolder: (bucketId: string, name: string) =>
    ApiClient.post<object>(`${basePath}/storage/${encodeURIComponent(bucketId)}/path`, { name }),

  deleteFolder: (bucketId: string, path: string) =>
    ApiClient.delete<object>(
      `${basePath}/storage/${encodeURIComponent(bucketId)}/path/${encodeNestedPath(path)}`,
    ),

  getFile: (bucketId: string, filePath: string, link = false, exp = 3600) =>
    ApiClient.get<Record<string, unknown> & { ACL: object; TagSet: object[]; Link?: object }>(
      `${basePath}/storage/${encodeURIComponent(bucketId)}/files/${encodeNestedPath(filePath)}`,
      { params: link ? { link: '', exp } : undefined },
    ),

  uploadFile: (
    bucketId: string,
    data: FormData,
    onProgress?: (progress: number) => void,
  ) => ApiClient.post<{ details: 'File uploaded' }>(
    `${basePath}/storage/${encodeURIComponent(bucketId)}/files`,
    data,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (event.total && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100))
        }
      },
    },
  ),

  updateFile: (bucketId: string, filePath: string, data: FileUpdate) =>
    ApiClient.put<object>(
      `${basePath}/storage/${encodeURIComponent(bucketId)}/files/${encodeNestedPath(filePath)}`,
      data,
    ),

  deleteFile: (bucketId: string, filePath: string) =>
    ApiClient.delete(
      `${basePath}/storage/${encodeURIComponent(bucketId)}/files/${encodeNestedPath(filePath)}`,
    ),

  getUsers: (params: ObjectUserListParams) =>
    ApiClient.get<PaginatedResponse<StaffObjectUserListItem>>(`${basePath}/users`, { params }),

  getUser: (objectUserId: string) =>
    ApiClient.get<StaffObjectUserDetails>(`${basePath}/users/${encodeURIComponent(objectUserId)}`),

  updateUser: (objectUserId: string, data: ObjectUserUpdate) =>
    ApiClient.patch<StaffObjectUserDetails>(`${basePath}/users/${encodeURIComponent(objectUserId)}`, data),

  getUserTokens: (objectUserId: string) =>
    ApiClient.get<SecretKey[]>(`${basePath}/users/${encodeURIComponent(objectUserId)}/tokens`),
}

export default staffObjectStorageService
